import React, { FC, Fragment, useContext, useState } from 'react';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { useTranslation } from 'react-i18next';
import { ApolloError, useMutation } from '@apollo/client';
import { unlinkAttachedScannerCaseTemplate } from 'apollo/templates/scanner-case';
import { ScannerCase } from 'models/scanner-case';
import InfoText from 'components/shared/info-text.component';
import { Box, Button } from '@mui/material';
import AttachScannerCaseDialog from 'components/scanner-cases/shared/attach-scanner-case-dialog/attach-scanner-case-dialog.component';
import ScannerCaseItem from './scanner-case-item.component';
import { ActionOrderContext } from 'pages/order-flow/action-order/action-order-context';
import { patientDisplayName } from 'utils/orders';

const AttachedScannerCases: FC = () => {
    const { t: translate } = useTranslation(['orders', 'three-shape']);
    const [deleteScanCaseAttachmentConfirmDialogOpen, setDeleteScanCaseAttachmentConfirmDialogOpen] = useState(false);
    const [unlinkScannerCaseId, setUnlinkScannerCaseId] = useState<string>();
    const [dentistAddScanDialogOpen, setDentistAddScanDialogOpen] = useState(false);
    const { order, setOrder } = useContext(ActionOrderContext);

    const [unlinkAttachedScannerCase] = useMutation(unlinkAttachedScannerCaseTemplate, {
        onCompleted: () => {
            const updatedCases = order.attachedScannerCaseFiles.filter(
                (scanCase) => scanCase._id !== unlinkScannerCaseId,
            );
            setOrder((prev) => ({ ...prev, attachedScannerCaseFiles: updatedCases }));
            setDeleteScanCaseAttachmentConfirmDialogOpen(false);
        },
        onError: (error: ApolloError) => {
            console.log(error, 'error');
        },
    });

    const handleOpenUnlinkDialog = (scanCaseId: string) => {
        setUnlinkScannerCaseId(scanCaseId);
        setDeleteScanCaseAttachmentConfirmDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDeleteScanCaseAttachmentConfirmDialogOpen(false);
    };

    const handleDeleteScanCaseAttachment = async () => {
        await unlinkAttachedScannerCase({
            variables: {
                orderId: order._id,
                scannerCaseId: unlinkScannerCaseId,
            },
        });
    };

    const handleAttachScannerCaseClick = () => {
        setDentistAddScanDialogOpen(true);
    };

    const closeScanCaseDialog = (scannerCase?: ScannerCase) => {
        if (scannerCase) {
            setOrder((prev) => ({
                ...prev,
                attachedScannerCaseFiles: [...prev.attachedScannerCaseFiles, scannerCase],
            }));
        }
        setDentistAddScanDialogOpen(false);
    };

    return (
        <Fragment>
            {order.attachedScannerCaseFiles && order.attachedScannerCaseFiles.length > 0 ? (
                <>
                    {order.attachedScannerCaseFiles?.map((scanCase: ScannerCase) => (
                        <ScannerCaseItem
                            key={scanCase._id}
                            scannerCase={scanCase}
                            order={order}
                            onOpenUnlinkDialog={handleOpenUnlinkDialog}
                        />
                    ))}
                </>
            ) : (
                <InfoText text={translate('orders:NO_ATTACHED_SCANS')} />
            )}
            <Box>
                <Button
                    onClick={handleAttachScannerCaseClick}
                    data-test="files-images-section.attach-intraoral-scan-button"
                >
                    {translate('orders:ATTACH_INTRAORAL_SCAN')}
                </Button>
            </Box>
            <ConfirmDialog
                open={deleteScanCaseAttachmentConfirmDialogOpen}
                handleClose={handleCloseDialog}
                dialogContent={translate('three-shape:CONFIRM_DELETE_SCAN_ATTACHMENT')}
                handleConfirm={handleDeleteScanCaseAttachment}
            />
            <AttachScannerCaseDialog
                orderId={order._id as string}
                pin={order.patient?.personalIdentityNumber || ''}
                name={patientDisplayName(order.patient)}
                open={dentistAddScanDialogOpen}
                onClose={closeScanCaseDialog}
            />
        </Fragment>
    );
};

export default AttachedScannerCases;
